import { useCallback, useEffect, useRef } from 'react';

import { useViewportSizeEffect } from '#/hooks/viewport/useViewportSizeEffect';
import { transition } from '#/theme/theme';

import type { Tab } from './Tab';

type Args<TValue extends string> = {
  tabs: Tab<TValue>[] | readonly Tab<TValue>[];
  value: TValue | undefined;
  onSelectTab?: (value: TValue) => void;
  hasMarker: boolean;
};

export function useTabsController<TValue extends string>({
  tabs,
  value,
  hasMarker,
}: Args<TValue>) {
  const selectedTabIndex = value
    ? tabs.findIndex((tab) => tab.value === value)
    : 0;

  const tabsHolderRef = useRef<HTMLUListElement>(null);
  const markerRef = useRef<HTMLDivElement>(null);

  const moveMarker = useCallback(
    (animate?: boolean) => {
      if (!hasMarker) return;

      const tabsContainer = tabsHolderRef.current;
      const marker = markerRef.current;

      if (!tabsContainer || !marker || selectedTabIndex === -1) return;

      const tabNode = tabsContainer.childNodes[selectedTabIndex];
      if (!tabNode) return;

      const { offsetLeft, offsetWidth, offsetTop, offsetHeight } =
        tabNode as HTMLDivElement;

      marker.style.width = `${offsetWidth}px`;
      marker.style.left = `${offsetLeft - tabsContainer.scrollLeft}px`;
      marker.style.top = `${
        offsetTop + offsetHeight - tabsContainer.scrollTop
      }px`;

      if (!animate) {
        marker.style.transition = 'none';
      }

      setTimeout(() => {
        marker.style.transition = transition;
      }, 0);
    },
    [selectedTabIndex, hasMarker],
  );

  useEffect(() => {
    moveMarker(true);
  }, [moveMarker]);

  useViewportSizeEffect(() => {
    moveMarker(true);
  });

  return { tabsHolderRef, markerRef, selectedTabIndex, moveMarker };
}
